import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, MaskText } from "./Reveal";
import { STAGES, stageAt } from "./stages";
import { useReserve } from "./Reserve";

const EASE = [0.22, 1, 0.36, 1];

// Each option carries a developmental score (1 = Conception … 7 = Maturity).
const QUESTIONS = [
  { area: "Revenue", q: "Are people paying you yet?", a: [
    ["No — it's still an idea", 1], ["A handful of first customers", 2], ["Yes, but sales are unpredictable", 3],
    ["Yes, consistently", 4.5], ["Yes — demand is growing faster than we can handle", 6],
  ]},
  { area: "Demand", q: "How consistent is demand?", a: [
    ["There's no demand to measure yet", 1], ["Sporadic — it comes in bursts", 2.5], ["Somewhat predictable month to month", 4],
    ["Reliable and growing", 5.5], ["Reliable for years", 7],
  ]},
  { area: "Customer", q: "How clearly can you describe your customer?", a: [
    ["I have a hunch", 1], ["I'm learning who actually buys", 2.5], ["I know who they are, but not fully why", 3.5],
    ["Clearly — and it shapes our decisions", 5], ["Several well-defined segments", 6.5],
  ]},
  { area: "Retention", q: "Do customers come back or refer others?", a: [
    ["Not applicable yet", 1], ["Occasionally", 3], ["A meaningful share do", 4.5], ["Repeat and referral drive our growth", 6.5],
  ]},
  { area: "Economics", q: "How would you describe profitability and cash flow?", a: [
    ["No revenue — I'm funding it myself", 1], ["Revenue, but cash is tight and unpredictable", 2.5], ["Roughly break-even", 4],
    ["Consistently profitable", 5.5], ["Profitable, with capital to deploy", 7],
  ]},
  { area: "Operations", q: "Where do your systems and operations stand?", a: [
    ["Everything lives in my head", 1.5], ["A few processes, mostly improvised", 3], ["Documented systems for the core work", 4.5],
    ["Systems that run without me daily", 6], ["Layers of management and reporting", 7],
  ]},
  { area: "Team", q: "Who is on the team?", a: [
    ["Just me", 1], ["Me plus a partner or freelancers", 2.5], ["A small team — everyone does everything", 3.5],
    ["A real team with defined roles", 5], ["A leadership team; I'm stepping back from day-to-day", 6.5],
  ]},
  { area: "Founder", q: "If you stepped away for a month, what would happen?", a: [
    ["It stops", 1.5], ["It slows dramatically", 3], ["It runs for a while", 4.5], ["It runs well without me", 6.5],
  ]},
  { area: "Growth", q: "What is your biggest growth challenge right now?", a: [
    ["Deciding whether to start at all", 1], ["Getting the first real evidence", 2], ["Making what's working repeatable", 3.5],
    ["Growing without breaking things", 5], ["People, leadership and capital", 6], ["What comes after me", 7],
  ]},
  { area: "Decision", q: "What major decision is in front of you?", a: [
    ["Should I pursue this at all?", 1], ["Should I go all in?", 2], ["What needs to become predictable?", 3],
    ["Which customer or market do we commit to?", 4], ["Should we hire, raise or expand?", 5.5], ["How do I lead instead of do?", 6], ["What's the next chapter — or the exit?", 7],
  ]},
];

const OUTCOMES = ["your current business stage", "what that stage typically needs", "what you may be trying to do too soon", "the biggest question your roadmap should answer next"];

const Result = ({ stage, onRestart }) => {
  const { open } = useReserve();
  return (
    <motion.div key="result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} data-testid="assessment-result">
      <div className="font-sans text-xs uppercase tracking-[0.22em] text-cognac">Your business is in</div>
      <div className="mt-4 flex items-baseline gap-5">
        <span className="font-serif text-7xl sm:text-8xl text-ocean/12 leading-none">{stage.n}</span>
        <h3 data-testid="assessment-result-title" className="font-serif text-4xl sm:text-5xl text-ocean tracking-tight leading-none">
          {stage.label === "Toddler Years" ? "The Toddler Years" : stage.label}
        </h3>
      </div>
      <p className="mt-6 max-w-2xl font-sans text-base sm:text-lg text-ink/75 leading-relaxed">{stage.body}</p>

      <div className="mt-8 grid sm:grid-cols-2 gap-6">
        <div className="border-t border-[var(--line)] pt-4">
          <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-cognac">What this stage needs</div>
          <p className="mt-2 font-sans text-sm text-ink/75 leading-relaxed">{stage.needs}</p>
        </div>
        <div className="border-t border-[var(--line)] pt-4">
          <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-cognac">What you may be doing too soon</div>
          <p className="mt-2 font-sans text-sm text-ink/75 leading-relaxed">{stage.tooSoon}</p>
        </div>
      </div>

      <div className="mt-8 border-t border-[var(--line)] pt-6 grid sm:grid-cols-[auto_1fr] gap-x-8 gap-y-4 items-center">
        <div>
          <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-ink/50">Your roadmap should focus less on</div>
          <p className="mt-1 font-serif text-lg text-ink/45 line-through decoration-cognac/50">“{stage.lessOn}”</p>
        </div>
        <div className="sm:border-l border-[var(--line)] sm:pl-8">
          <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-cognac">and more on</div>
          <p className="mt-1 font-serif italic text-2xl sm:text-3xl text-ocean leading-snug">“{stage.moreOn}”</p>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-5">
        <button
          data-testid="assessment-build-btn"
          onClick={() => open("quiz")}
          className="group font-sans text-sm uppercase tracking-[0.18em] bg-ocean text-cream px-8 py-4 hover:bg-cognac transition-colors duration-300"
        >
          Build My Roadmap
          <span className="inline-block ml-3 transition-transform duration-300 group-hover:translate-x-1">→</span>
        </button>
        <button data-testid="assessment-restart" onClick={onRestart} className="link-underline font-sans text-sm text-ocean">
          Retake the quiz
        </button>
      </div>
    </motion.div>
  );
};

export const Assessment = () => {
  const { setStage } = useReserve();
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState([]);

  const total = QUESTIONS.length;
  const done = started && step >= total;
  const progress = !started ? 0 : Math.round((Math.min(step, total) / total) * 100);

  const pick = (score) => {
    const next = [...scores.slice(0, step), score];
    setScores(next);
    if (step + 1 >= total) {
      const avg = next.reduce((a, b) => a + b, 0) / next.length;
      setStage(stageAt(avg).label);
    }
    setStep(step + 1);
  };

  const restart = () => {
    setScores([]);
    setStep(0);
    setStarted(false);
    setStage(null);
  };

  const result = done ? stageAt(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

  return (
    <section id="assessment" className="relative bg-ocean text-cream py-24 sm:py-32 overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -bottom-10 right-0 font-serif italic text-[18vw] leading-none text-cream/[0.03] select-none">
        stage
      </div>

      <div className="relative mx-auto max-w-[1300px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-y-12 gap-x-12">
          <div className="lg:col-span-5">
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">The Founder Stage Quiz</span>
            <h2 className="mt-6 font-serif tracking-[-0.02em] leading-[0.96] text-4xl sm:text-5xl lg:text-[4.2rem]">
              <MaskText lines={["What stage is", "your business", <span key="i" className="italic text-cognac">in?</span>]} />
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-7 max-w-md font-sans text-base sm:text-lg text-cream/75 leading-relaxed">
                Before you build a roadmap, you need to know where you're starting. Take the short
                Founder Stage Quiz to see where your business — or the specific initiative you're
                working on — sits in Brian's lifecycle.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 border-t border-white/15 pt-6">
                <div className="font-sans text-[11px] uppercase tracking-[0.22em] text-cream/50">You'll get</div>
                <ul className="mt-3 space-y-2">
                  {OUTCOMES.map((o) => (
                    <li key={o} className="flex items-start gap-3 font-sans text-sm text-cream/80">
                      <span className="mt-2 h-1 w-1 bg-cognac shrink-0" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="border border-white/15 bg-cream text-ink">
                <div className="h-1 w-full bg-[var(--line)]">
                  <motion.div className="h-full bg-cognac" animate={{ width: `${progress}%` }} transition={{ duration: 0.5, ease: EASE }} />
                </div>

                <div className="p-7 sm:p-12 min-h-[480px] flex flex-col justify-center">
                  <AnimatePresence mode="wait">
                    {!started ? (
                      <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.45, ease: EASE }} data-testid="assessment-intro">
                        <div className="font-sans text-xs uppercase tracking-[0.2em] text-cognac">Ten questions · about two minutes</div>
                        <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-ocean tracking-tight leading-tight max-w-xl">
                          This isn't a personality quiz. It's the first step of the methodology.
                        </h3>
                        <p className="mt-5 max-w-xl font-sans text-base text-ink/70 leading-relaxed">
                          Answer honestly about revenue, demand, customers, cash, systems, team and the decision in front of you.
                          If you're assessing a new initiative inside an established company, answer for the initiative.
                        </p>
                        <button
                          data-testid="assessment-start-btn"
                          onClick={() => setStarted(true)}
                          className="mt-9 group font-sans text-sm uppercase tracking-[0.18em] bg-ocean text-cream px-8 py-4 hover:bg-cognac transition-colors duration-300"
                        >
                          Take the 2-Minute Quiz
                          <span className="inline-block ml-3 transition-transform duration-300 group-hover:translate-x-1">→</span>
                        </button>
                      </motion.div>
                    ) : !done ? (
                      <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.45, ease: EASE }} data-testid={`assessment-q-${step}`}>
                        <div className="flex items-center justify-between font-sans text-xs uppercase tracking-[0.2em] text-cognac mb-4">
                          <span>Question {step + 1} of {total}</span>
                          <span className="text-ink/40">{QUESTIONS[step].area}</span>
                        </div>
                        <h3 className="font-serif text-2xl sm:text-3xl text-ocean tracking-tight leading-snug max-w-2xl">
                          {QUESTIONS[step].q}
                        </h3>
                        <div className="mt-8 grid sm:grid-cols-2 gap-3">
                          {QUESTIONS[step].a.map(([t, s], i) => (
                            <button
                              key={t}
                              data-testid={`assessment-option-${step}-${i}`}
                              onClick={() => pick(s)}
                              className="group text-left font-sans text-[15px] text-ink/80 border border-[var(--line)] px-5 py-4 hover:border-cognac hover:bg-sand transition-colors duration-300 flex items-start gap-3"
                            >
                              <span className="mt-1.5 h-1.5 w-1.5 bg-cognac/40 group-hover:bg-cognac shrink-0 transition-colors" />
                              <span>{t}</span>
                            </button>
                          ))}
                        </div>
                        {step > 0 && (
                          <button data-testid="assessment-back" onClick={() => setStep(step - 1)} className="mt-8 link-underline font-sans text-sm text-ocean/60">
                            ← Back
                          </button>
                        )}
                      </motion.div>
                    ) : (
                      <Result stage={result} onRestart={restart} />
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export { STAGES };
